import type {
  ClientMediaItem,
  ClientOrganization
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ModalProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import Collapsible from 'react-native-collapsible';
import { Text } from 'react-native-paper';

import {
  CallIcon,
  LinkIcon,
  LocationIcon,
  MailIcon,
  PersonIcon
} from '@/assets';
import { ListItem } from '@/features/list/components/ListItem';
import { ListSection } from '@/features/list/components/ListSection';
import { ProfileAvatar } from '@/features/media/components/avatar/ProfileAvatar';
import { MediaItemSize } from '@/features/media/types/mediaItem';
import { PreviewImageCarousel } from '@/features/profile-setting/components/organization-edit/PreviewImageCarousel';
import { PreviewModalShell } from '@/features/profile-setting/components/organization-edit/PreviewModalShell';
import type { OrganizationEditFormValues } from '@/features/profile-setting/components/organization-edit/types';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { ShowMoreButton } from '@/shared/components/ShowMoreButton';
import { hasItems } from '@/shared/utils/array';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const COLLAPSED_DESCRIPTION_HEIGHT = 168;

type Props = Omit<ModalProps, 'children'> & {
  /**
   * The organization the user is editing. Passed through to the shell for
   * the header title (region name resolution) and used for the persisted
   * logo fallback.
   */
  organization?: ClientOrganization;
  /**
   * The organization's persisted logo. Rendered when the user hasn't just
   * picked a new one via the avatar menu (that fresh pick is on the form).
   */
  currentLogoImage?: ClientMediaItem;
};

export const OrganizationPreviewModal: FC<Props> = ({
  organization,
  currentLogoImage,
  ...modalProps
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-edit.preview'
  });

  const { control } = useFormContext<OrganizationEditFormValues>();
  const name = useWatch({ control, name: 'name' }) ?? '';
  const address = useWatch({ control, name: 'address' }) ?? '';
  const emailAddress = useWatch({ control, name: 'emailAddress' }) ?? '';
  const phone = useWatch({ control, name: 'phone' }) ?? '';
  const url = useWatch({ control, name: 'url' }) ?? '';
  const description = useWatch({ control, name: 'descriptionText' }) ?? '';
  const detailImages = useWatch({ control, name: 'detailImages' }) ?? [];
  const facts = useWatch({ control, name: 'facts' }) ?? [];
  const logoResult = useWatch({ control, name: 'logoResult' });

  const [expanded, setExpanded] = useState(false);
  const [descriptionFullHeight, setDescriptionFullHeight] = useState(0);
  const descriptionOverflows =
    descriptionFullHeight > COLLAPSED_DESCRIPTION_HEIGHT;

  useEffect(() => {
    // Reset the collapsible when the modal closes so it starts collapsed next time.
    // oxlint-disable-next-line react/set-state-in-effect
    if (!modalProps.visible) setExpanded(false);
  }, [modalProps.visible]);

  useEffect(() => {
    // Description content changed — remeasure on next layout.
    // oxlint-disable-next-line react/set-state-in-effect
    setDescriptionFullHeight(0);
  }, [description]);

  const iconProps = {
    fill: theme.colors.primary,
    width: 24,
    height: 24
  };

  const displayLogoMediaItem = logoResult?.localImage
    ? undefined
    : (currentLogoImage ?? undefined);
  const displayLogoLocalUri = logoResult?.localImage?.uri;

  return (
    <PreviewModalShell
      {...modalProps}
      organization={organization}
      scrollContentContainerStyle={styles.scrollExtras}
    >
      <View style={styles.orgHeader}>
        <ProfileAvatar
          viewSize={120}
          iconFallback={PersonIcon}
          mediaItem={displayLogoMediaItem}
          localImageUri={displayLogoLocalUri}
          mediaSize={MediaItemSize.MEDIUM}
        />
        <View style={styles.orgHeaderTextWrapper}>
          <Text
            variant="headlineMedium"
            numberOfLines={2}
            style={styles.centered}
          >
            {name}
          </Text>
        </View>

        <ListSection style={styles.list}>
          {!isEmptyOrNull(address) && (
            <ListItem
              icon={() => <LocationIcon {...iconProps} />}
              title={address}
              hideRightArrow
            />
          )}
          {!isEmptyOrNull(emailAddress) && (
            <ListItem
              icon={() => <MailIcon {...iconProps} />}
              title={emailAddress}
              hideRightArrow
            />
          )}
          {!isEmptyOrNull(phone) && (
            <ListItem
              icon={() => <CallIcon {...iconProps} />}
              title={phone}
              hideRightArrow
            />
          )}
          {!isEmptyOrNull(url) && (
            <ListItem
              icon={() => <LinkIcon {...iconProps} />}
              title={url}
              hideRightArrow
            />
          )}
        </ListSection>
      </View>

      <View style={styles.body}>
        {hasItems(detailImages) && (
          <PreviewImageCarousel entries={detailImages} />
        )}

        {!isEmptyOrNull(description) && (
          <View>
            <Text variant="titleLarge" style={styles.sectionTitle}>
              {t('about')}
            </Text>
            <Collapsible
              collapsed={!expanded}
              collapsedHeight={COLLAPSED_DESCRIPTION_HEIGHT}
              renderChildrenCollapsed
            >
              <Text variant="bodyLarge">{description}</Text>
            </Collapsible>
            {/* Hidden measurement copy: lets us know the fully-expanded
                height so we can decide whether the ShowMore button is
                needed. `aria-hidden` keeps it out of screen readers. */}
            <View
              style={[styles.descriptionMeasurement, { pointerEvents: 'none' }]}
              aria-hidden
              accessibilityElementsHidden
              importantForAccessibility="no-hide-descendants"
            >
              <Text
                variant="bodyLarge"
                onLayout={(e) =>
                  setDescriptionFullHeight(e.nativeEvent.layout.height)
                }
              >
                {description}
              </Text>
            </View>
            {descriptionOverflows && (
              <View style={styles.showMoreWrapper}>
                <ShowMoreButton
                  expanded={expanded}
                  onChange={() => setExpanded(!expanded)}
                />
              </View>
            )}
          </View>
        )}

        {hasItems(facts) && (
          <View>
            <Text variant="titleLarge" style={styles.sectionTitle}>
              {t('facts')}
            </Text>
            {facts.map((fact) => (
              <ListItem
                color={theme.colors.onBackground}
                key={fact.key}
                title={fact.name}
                badge={fact.value}
                hideRightArrow
              />
            ))}
          </View>
        )}
      </View>
    </PreviewModalShell>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    scrollExtras: {
      gap: 16
    },
    orgHeader: {
      alignItems: 'center',
      gap: 16,
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 32,
      // Beige header band: the white background starts below, at the carousel.
      backgroundColor: theme.colors.surfaceContainer
    },
    orgHeaderTextWrapper: { alignItems: 'center' },
    centered: { textAlign: 'center' },
    list: { width: '100%', backgroundColor: 'transparent' },
    body: {
      paddingVertical: 32,
      paddingHorizontal: 16,
      gap: 32
    },
    sectionTitle: { color: theme.colors.onSurface, paddingBottom: 16 },
    showMoreWrapper: { alignSelf: 'flex-start', paddingTop: 16 },
    descriptionMeasurement: {
      position: 'absolute',
      opacity: 0,
      // Match the Collapsible's parent width so wrapping matches. Left/right 0
      // stretches to the layout width the visible copy occupies.
      left: 0,
      right: 0,
      top: 0
    }
  });
